import { useEffect, useState, useCallback } from 'react';
import { debounce } from 'shared/utils/debounce';
import { css } from 'shared/utils';
export function useScale(scaleChecker, containerRef, props) {
    var _a, _b, _c;
    const [scale, setScale] = useState(undefined);
    const clientRect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
    const width = (_b = clientRect) === null || _b === void 0 ? void 0 : _b.width;
    const height = (_c = clientRect) === null || _c === void 0 ? void 0 : _c.height;
    const checkForOverflow = useCallback(() => {
        if (!containerRef.current) {
            return;
        }
        const updatedScale = scaleChecker(containerRef.current, props);
        if (scale !== updatedScale) {
            setScale(updatedScale);
        }
    }, [containerRef, props, scale, scaleChecker]);
    // Re-check for overflow when the window
    // is resized or fonts are loaded.
    useEffect(() => {
        const debouncedCheckForOverflow = debounce(checkForOverflow, 25, true);
        // Avoid layout shifts, If not in editor, we will wait for a `renderRequested` event.
        if (props.inEditor) {
            debouncedCheckForOverflow();
        }
        window.addEventListener('resize', debouncedCheckForOverflow);
        document.addEventListener('renderRequested', debouncedCheckForOverflow);
        return () => {
            window.removeEventListener('resize', debouncedCheckForOverflow);
            document.removeEventListener('renderRequested', debouncedCheckForOverflow);
        };
    }, [
        checkForOverflow,
        props,
        // These are not used directly, but still affect the results.
        width,
        height,
    ]);
    return [scale, checkForOverflow];
}
/**
 * Clone a hidden version of an HTMLElement
 * that can be used to calculate for overflow.
 */
export function createOverFlowRuler(element, restStateStyles) {
    const ruler = element.cloneNode(true);
    ruler.id = Math.random()
        .toString(36)
        .substring(7);
    const parent = element.parentElement || document.body;
    css(ruler, Object.assign(Object.assign({}, restStateStyles), { display: 'block', opacity: 0, overflow: 'hidden', transform: '' }));
    parent.appendChild(ruler);
    const unmountRuler = () => parent.removeChild(ruler);
    return [ruler, unmountRuler];
}
